/* ManageImagesRenderer.js */

"use strict";
const _ = require("lodash");
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../../../RegistrationNamespaces");

class ManageImagesRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("manage-images-view-gl");
        this._rootElem.component = this.model;
        this._logger = this._model._logger;

        this._imagesSection = undefined;
        this._emptyGridContainer = document.createElement("div");
        this._onUserSorted = this._onUserSorted.bind(this);
        this._onFeedRefreshView = this._onFeedRefreshView.bind(this);
        this._imageEntityGrid = undefined;
        this._defaultViewActive = false;

        this._imagesSectionContainer = document.createElement("div");
        this._imagesSectionContainer.classList.add("image-grid-section");

        // Lines number display container
        this._linesNumberContainer = document.createElement("div");
        this._linesNumberContainer.classList.add("lines-number-container");

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        // Add event listeners here if needed
    }

    _unregisterListeners() {
        // Remove event listeners here if needed
    }

    _registerGridListeners() {
        this._model.pagingFeed.on("RefreshView", this._onFeedRefreshView);
        this._imageEntityGrid.on("UserSorted", this._onUserSorted);
    }

    _unregisterGridListeners() {
        this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        if (this._imageEntityGrid) {
            this._imageEntityGrid.removeListener("UserSorted", this._onUserSorted);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    _onUserSorted(eventData) {
        this._model._onUserSorted(eventData);
    }

    async shutdown() {
        this._unregisterListeners();
        this._unregisterGridListeners();

        if (this._defaultViewActive === true) {
            await this._hideDefaultMessageDisplay().catch(() => {});
        }

        const promArr = [
            _.isUndefined(this._imageEntityGrid)
                ? Promise.resolve()
                : this._imageEntityGrid.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                // Clean up grid reference
                this._imageEntityGrid = undefined;

                // Clean up DOM elements
                if (this._imagesSectionContainer) {
                    this._imagesSectionContainer.remove();
                    this._imagesSectionContainer = undefined;
                }
                if (this._linesNumberContainer) {
                    this._linesNumberContainer.remove();
                    this._linesNumberContainer = undefined;
                }
                if (this._emptyGridContainer) {
                    this._emptyGridContainer.remove();
                    this._emptyGridContainer = undefined;
                }

                // Clean up state
                this._defaultViewActive = undefined;
                this._initialEntityCount = undefined;
                this._imagesSection = undefined;

                // Clean up references
                this._logger = undefined;
                this._model = undefined;

                // Clean up root element last
                if (this._rootElem) {
                    this._rootElem.remove();
                    this._rootElem = undefined;
                }
            },
            (err) => {
                if (this._logger) {
                    this._logger.warn(err, "ManageImagesRenderer.shutdown() error");
                }
            }
        );
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;

        if (totalEntities === 0) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     */
    async render() {
        await this._model.whenModelIsReady();

        this._rootElem.appendChild(this._imagesSectionContainer);
        this._rootElem.appendChild(this._linesNumberContainer);
        this.rootElement.appendChild(this._emptyGridContainer);
        this._initialEntityCount = this._model.pagingFeed.totalEntities;

        this._renderImages();
        this._registerListeners();

        return;
    }

    _renderImages() {
        this._imagesSectionContainer.innerHTML = "";

        this._imageEntityGrid = new EntityGrid({
            gridKey: this.model._key,
            logger: this._logger,
            singleSelect: true,
            columns: this._model.columnConfig,
            pagingFeed: this._model.pagingFeed,
            showHeader: true,
            showLinesNumber: this._linesNumberContainer,
            modalUserFeedback: this._model._modalUserFeedback,
            userGridData: this._model._userGridData,
        });
        // create entity grid and render it within this._imagesSectionContainer
        this._imagesSectionContainer.appendChild(this._imageEntityGrid.renderer.rootElement);
        this._registerGridListeners();

        this._onFeedRefreshView();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _showDefaultMessageDisplay() {
        const message = {
            message: "Add an Image",
            icon: "osmanager.add_images",
            action: Registrations.ODM_OS_PLATFORM_EDITOR_ACTIONS_ADD_IMAGES,
        };
        return this._model._gameEditor
            .showDefaultMessageView(message, this._imagesSectionContainer)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageImagesRenderer._showDefaultMessageDisplay error"
                );
            });
    }
    _hideDefaultMessageDisplay() {
        return this._model._gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageImagesRenderer._hideDefaultMessageDisplay error"
                );
                throw err;
            });
    }
}

module.exports = ManageImagesRenderer;
